import { resolveApiMediaUrl } from '../../../api/client.js';
import { firstDisplayImageRaw } from '../../../utils/productImage.js';

/**
 * @param {{
 *   product: Record<string, unknown> | null,
 *   open: boolean,
 *   onClose: () => void,
 *   qty: number,
 *   formatBrl: (n: number) => string,
 *   pricingHidden: boolean,
 *   catalogOnly: boolean,
 *   soldOut: boolean,
 *   atStockCap: boolean,
 *   onAddQty: (delta: number) => void,
 *   onRequireLogin: () => void,
 *   onOpenGallery?: () => void,
 *   hasGallery: boolean,
 *   productImageCtx?: import('../../../utils/productImage.js').ProductImageContext,
 * }} props
 */
export function CatalogLuxeProductModal({
  product,
  open,
  onClose,
  qty,
  formatBrl,
  pricingHidden,
  catalogOnly,
  soldOut,
  atStockCap,
  onAddQty,
  onRequireLogin,
  onOpenGallery,
  hasGallery,
  productImageCtx,
}) {
  if (!open || !product) return null;

  const nome = String(product.nome || 'Produto');
  const desc = String(product.descricao || '').trim();
  const preco = Number(product.preco || 0);
  const erp = product.codigo_erp != null && String(product.codigo_erp).trim() !== '' ? String(product.codigo_erp).trim() : '';
  const code = erp || String(product.id || '').padStart(6, '0');
  const imgRaw = firstDisplayImageRaw(product, productImageCtx);
  const img = imgRaw ? resolveApiMediaUrl(imgRaw) : '';

  return (
    <div className="cl-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cl-modal-panel" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--cl-border)] text-[var(--cl-muted)] hover:text-[var(--cl-fg)]"
          onClick={onClose}
          aria-label="Fechar"
        >
          ✕
        </button>
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square min-h-[280px] bg-[oklch(0.22_0.1_20)]">
            {img ? (
              <button
                type="button"
                className="h-full w-full"
                onClick={hasGallery && onOpenGallery ? onOpenGallery : undefined}
                disabled={!hasGallery || !onOpenGallery}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[var(--cl-muted)]">Sem imagem</div>
            )}
            <div className="absolute left-4 top-4 rounded-full border border-[oklch(0.78_0.13_80/30)] bg-[oklch(0.16_0.02_25/80)] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[var(--cl-gold)]">
              {code}
            </div>
          </div>
          <div className="flex flex-col justify-between p-8 md:p-10">
            <div>
              <h3 className="cl-display text-3xl md:text-4xl">{nome}</h3>
              <div className="cl-divider my-6 w-16" />
              {desc ? <p className="text-sm leading-relaxed text-[var(--cl-muted)]">{desc}</p> : null}
              {!pricingHidden ? (
                <div className="mt-8 rounded-xl border border-[var(--cl-border)] bg-[oklch(0.16_0.02_25/60)] p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--cl-muted)]">Preço</div>
                  <div className="cl-display cl-gold-text mt-1 text-4xl">{formatBrl(preco)}</div>
                </div>
              ) : null}
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {!catalogOnly ? (
                pricingHidden ? (
                  <button type="button" className="cl-btn-primary flex-1" onClick={onRequireLogin}>
                    Entrar para comprar
                  </button>
                ) : soldOut ? (
                  <p className="text-sm font-semibold text-amber-400">Sem stock no momento</p>
                ) : qty <= 0 ? (
                  <button type="button" className="cl-btn-primary flex-1" onClick={() => onAddQty(1)}>
                    Adicionar à sacola
                  </button>
                ) : (
                  <div className="flex flex-1 items-center justify-center gap-3">
                    <button
                      type="button"
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--cl-border)] text-lg font-bold"
                      onClick={() => onAddQty(-1)}
                    >
                      −
                    </button>
                    <span className="min-w-[2rem] text-center text-lg font-bold tabular-nums">{qty}</span>
                    <button
                      type="button"
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--cl-gold)] text-lg font-bold text-[oklch(0.18_0.02_25)]"
                      onClick={() => onAddQty(1)}
                    >
                      +
                    </button>
                  </div>
                )
              ) : null}
              <button type="button" className="cl-btn-outline" onClick={onClose}>
                Continuar navegando
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
